import {
  Typography,
  ChipProps,
  Grid,
  Box,
  Tooltip,
  Button,
  Stack,
  MenuItem,
  SxProps,
  Theme,
  Divider,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IStoreState } from "../../../redux/initialStoreState";
import { useDispatchWrapper, useWhatsappCounts } from "../../../hooks";
import { useTableV2State } from "../../../components/TableV2/hooks/useTableV2State";
import { TABLE_CACHING_KEYS } from "../../../constants/constants";
import { fetchMarketingCampaignLogsAsync } from "../../../redux/marketCampaign/marketCampaignActions";

import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../components/TableV2/interfaces/IDataTableV2Props";
import { LoadState } from "../../../constants/enums";
import {
  IMarketCampaignLogs,
  IMarketCampain,
  IMarketCampaignBasicInfo,
} from "../../../redux/marketCampaign/marketCampaign.types";
import { CustomChip } from "../../../components/CustomChip";
import { createTabsWithWhatsappcounts } from "../../../helpers";
import {
  clearWhatsappCounts,
  fetchWhatsappCountsAsync,
} from "../../../redux/common/commonActions";
import { DataTableV2 } from "../../../components/TableV2/DataTableV2";
import { useBillingCompanyContext } from "../../../providers";
import { StandardTableActions } from "../../../components/StandardTableActions/StandardTableActions";
import { CSVNew } from "../../../components/TableV2/preDefinedPlugins/CSVExportV2/CSVNew";
import {
  STATUS_OPTIONS,
  processWhatsappCounts,
} from "../constants/statusConfig";
import { api } from "../../../api/api";
import { ILead } from "../../../redux/leads/leads.types";
import { ViewWhatsappDetails } from "../../leads/components/viewWhatsappDetails";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DataTableV2MobileFilter } from "../../../components/TableV2/components/mobileDataTableV2CommandBar/MobileFilter";
import {
  CustomFormLabel,
  CustomSelect,
  CustomTextField,
} from "../../../components/formsComponents";
import { IWhatsAppTemplate } from "../../../redux/whatsappTemplate/whatsappTemplate.types";
import { StandardCard } from "../../../components/Cards";
import { StatusRenderer } from "../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import CampaignIcon from "@mui/icons-material/Campaign";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { ViewWhatsappTemplateDetails } from "../../WhatsAppTemplate/components/ViewWhatsappTemplateDetails";
import { ReadMoreText } from "../../marketingCampaign/components/ReadMoreText";
import { StatusBarGraph } from "../../marketingCampaign/components/Graphs/StatusBarGraph";
import { PieChart } from "../../marketingCampaign/components/Graphs/PieChart";
import { RightPanel } from "../../../components/RightPanel";
import { Close } from "@mui/icons-material";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";

interface IMarketCampaignAnalyticsRightPanelProps {
  open: boolean;
  campaignData: IMarketCampaignBasicInfo | IMarketCampain | any;
  onClose: () => void;
}

export const MarketCampaignAnalyticsRightPanel: React.FC<
  IMarketCampaignAnalyticsRightPanelProps
> = ({ open, campaignData, onClose }) => {
  const dispatch = useDispatchWrapper();
  const { ftDateTime } = useTimezoneFormatter();

  const MarketCampaignUuid = campaignData?.market_campaign_uuid || "";

  const { list, loading } = useSelector(
    (storeState: IStoreState) => storeState.marketCampaign.marketCampaignLogs,
  );

  const { list: whatsappCounts, loading: recordCountsLoading } =
    useWhatsappCounts();

  const companyOutlet = useBillingCompanyContext();
  const company_outlet_uuid = companyOutlet?.comapny.company_outlet_uuid;

  const [openView, setOpenView] = React.useState<IWhatsAppTemplate | null>(
    null,
  );
  const [openWhatsapp, setOpenWhatsapp] = React.useState<{
    leadData: ILead | null;
    campaignLogsData: IMarketCampaignLogs | null;
  }>({
    leadData: null,
    campaignLogsData: null,
  });

  const {
    state: {
      dateState,
      searchState,
      tabs,
      pagination,
      columnsConfig,
      masterColumnResize,
    },
    state,
    setDateState,
    setSelectedTab,
    setTableTabs,
    setSearchState,
    setColumnVisibility,
    onPageChange,
    onRowsPerPageChange,
    onResizeMasterColumns,
    resetState,
    setState,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last30Days",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1,
    },
    cachingFilters: {
      uniqueKey: TABLE_CACHING_KEYS.MARKET_CAMPAGIN_LOGS,
    },
    otherCustomStateProperties: {
      searchByVenue: "",
    },
  });

  const fetchList = async () => {
    if (!MarketCampaignUuid) return;
    dispatch(
      fetchMarketingCampaignLogsAsync(
        MarketCampaignUuid,
        pagination.pageNumber,
        pagination.rowsInPerPage,
        pagination.apiFetchRowCount,
        tabs.selectedTab,
        dateState.dates,
      ),
    );
  };

  const fetchAllData = async () => {
    try {
      let apiURL = `/campaign/get-campaign-logs?market_campaign_uuid=${MarketCampaignUuid}`;

      if (tabs.selectedTab !== "-1") {
        apiURL += `&status=${tabs.selectedTab}`;
      }

      const res = await api.get(apiURL);
      return res.data?.data || [];
    } catch (err: any) {
      console.error("Error fetching data: ", err);
      return [];
    }
  };

  const opportunitesTableProps: IDataTableV2Props = {
    isPagination: true,
    rowsPerPageOptions: pagination.rowsInPerPage,
    isDataLoading: loading === LoadState.InProgress,
    extraFetchFactor: pagination.extraFetchFactor,
    tableCommandBarProps: {
      preDefinedPlugins: {
        // dateFilter: {
        //   state: dateState,
        //   onChange: setDateState,
        // },
        refresh: {
          onClick: () => {
            fetchList();
          },
        },
      },
      leftItems: {
        customPlugins: [],
      },
      rightItems: {
        customPlugins: [
          {
            key: "csvExport",
            onRender: (columns, items) => (
              <CSVNew
                columns={[
                  {
                    columnName: "Status",
                    fieldName: "status",
                    width: 20,
                  },
                  {
                    columnName: "Recipient Name",
                    fieldName: "recipient_name",
                    width: 20,
                  },
                  {
                    columnName: "Recipient Number",
                    fieldName: "recipient_number",
                    width: 20,
                  },
                  {
                    columnName: "Template Name",
                    fieldName: "template_name",
                    width: 20,
                  },
                  {
                    columnName: "Initiate At",
                    fieldName: "create_ts",
                    width: 20,
                  },
                  {
                    columnName: "Read At",
                    fieldName: "read_ts",
                    width: 20,
                  },
                  {
                    columnName: "Send At",
                    fieldName: "send_ts",
                    width: 20,
                  },
                  {
                    columnName: "Delivered At",
                    fieldName: "delivered_ts",
                    width: 20,
                  },
                ]}
                onExportButtonClick={fetchAllData}
                filePrefixName="Campaign Logs Report"
              />
            ),
          },
        ],
      },
    },
    initialSortConfig: {
      insert_ts: {
        direction: "desc",
        filedName: "insert_ts",
      },
    },
    mobileColumns: [
      {
        variant: "template1",
        subSectionColumn: [],
        otherColumns: [
          {
            key: "template_name",
            headerName: "Template Name",
            fieldName: "template_name",
          },
          {
            key: "status",
            fieldName: "status",
            headerName: "Status",
            onRowCellRender: (value) => {
              return (
                <CustomChip
                  content={typeof value === "string" ? value : String(value)}
                  color="warning"
                  size="small"
                />
              );
            },
          },
          {
            key: "recipient_number",
            headerName: "Recipient Number",
            // fieldName: "to_number",
            fieldName: "recipient_number", // monika updated this
          },
          {
            key: "create_ts",
            headerName: "Initiate At",
            fieldName: "create_ts",
            onRowCellRender: (value, row) => {
              const formattedDate = ftDateTime(row.create_ts);
              return row.create_ts ? formattedDate : "--";
            },
          },
          {
            key: "read_ts",
            headerName: "Read At",
            fieldName: "read_ts",
            onRowCellRender: (value, row) => {
              const formattedDate = ftDateTime(row.read_ts);
              return row.read_ts ? formattedDate : "--";
            },
          },
          {
            key: "send_ts",
            headerName: "Send At",
            fieldName: "send_ts",
            onRowCellRender: (value, row) => {
              const formattedDate = ftDateTime(row.send_ts);
              return row.send_ts ? formattedDate : "--";
            },
          },
          {
            key: "delivered_ts",
            headerName: "Delivered At",
            fieldName: "delivered_ts",
            onRowCellRender: (value, row) => {
              const formattedDate = ftDateTime(row.delivered_ts);
              return row.delivered_ts ? formattedDate : "--";
            },
          },
        ],
        actions: [
          {
            name: "View",
            icon: <VisibilityIcon />,
            click: (rowData: IWhatsAppTemplate) =>
              setOpenView(rowData as IWhatsAppTemplate),
          },
        ],
      },
    ],
    mobileCommandBarProps: {
      filterComponent: (
        <DataTableV2MobileFilter
          state={state}
          onApply={(newState) => {
            setState(newState);
            fetchList();
          }}
          enableDateFilter
          onClear={() => {
            resetState();
            fetchList();
          }}
        >
          {(filterLocalState, setFilterlocalState) => {
            return (
              <Stack direction={"column"} spacing={1}>
                <Box>
                  <CustomFormLabel>Status</CustomFormLabel>
                  <CustomSelect
                    fullWidth
                    placeholder="Select Status"
                    value={filterLocalState.tabs.selectedTab}
                    onChange={(e) => {
                      setFilterlocalState({
                        ...filterLocalState,
                        tabs: {
                          ...filterLocalState.tabs,
                          selectedTab: e.target.value as string,
                        },
                      });
                    }}
                  >
                    <MenuItem value="-1">All</MenuItem>
                    <MenuItem value="INITIATE">Initiate</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                    <MenuItem value="INVALID_NUMBER">Number Invalid</MenuItem>
                    <MenuItem value="READ">Read</MenuItem>
                    <MenuItem value="SENT">Sent</MenuItem>
                    <MenuItem value="DELIVERED">Delivered</MenuItem>
                    <MenuItem value="RECEIVED">Received</MenuItem>
                    <MenuItem value="BLOCKED_MARKETING">
                      Blocked Marketing
                    </MenuItem>
                  </CustomSelect>
                </Box>
                <Box>
                  <CustomFormLabel>Recipient Number</CustomFormLabel>
                  <CustomTextField
                    variant="outlined"
                    fullWidth
                    placeholder="Search by Number"
                    value={
                      filterLocalState.otherCustomStateProperties
                        ?.searchItems?.[0]?.value || ""
                    }
                    onChange={(e) => {
                      setFilterlocalState({
                        ...filterLocalState,
                        otherCustomStateProperties: {
                          ...filterLocalState.otherCustomStateProperties,
                          searchItems: [
                            {
                              column: ["recipient_number"],
                              value: e.target.value,
                              logicalOperator: "AND",
                              operator: "CONTAINS",
                            },
                          ],
                        },
                      });
                    }}
                  />
                </Box>
              </Stack>
            );
          }}
        </DataTableV2MobileFilter>
      ),
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        isFirstColumnSticky: true,
        width: "100px",
        onRowCellRender: (value, row: IWhatsAppTemplate | ILead) => {
          return (
            <StandardTableActions
              onViewClick={() => setOpenView(row as IWhatsAppTemplate)}
              onWhatsappClick={() => {
                setOpenWhatsapp({
                  leadData: row as ILead,
                  campaignLogsData: row as IMarketCampaignLogs,
                });
              }}
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IMarketCampain) => {
          const statusLabels: Array<{
            label: string;
            value: string;
            variant: ChipProps["color"];
            sx?: SxProps<Theme>;
          }> = [
              {
                label: "Initiate",
                value: "INITIATE",
                variant: "warning",
              },
              {
                label: "Failed",
                value: "FAILED",
                variant: "error",
              },
              {
                label: "Number Invalid",
                value: "INVALID_NUMBER",
                variant: "primary",
              },
              {
                label: "Read",
                value: "READ",
                variant: "success",
              },
              {
                label: "Sent",
                value: "SENT",
                variant: "info",
              },
              {
                label: "Delivered",
                value: "DELIVERED",
                variant: "secondary",
              },
              {
                label: "Received",
                value: "RECEIVED",
                variant: "default",
                sx: { backgroundColor: "#ff6116" },
              },
              {
                label: "Blocked Marketing",
                value: "BLOCKED_MARKETING",
                variant: "default",
                sx: {
                  backgroundColor: "#ffe9e9 !important",
                  color: "#ff5959 !important",
                  borderColor: "#ff5959 !important",
                },
              },
            ];

          const status = statusLabels.find(
            (status) => status.value === row.status,
          );

          return (
            <CustomChip
              color={(status?.variant as ChipProps["color"]) || "default"}
              content={status?.label || row.status}
              size="small"
              sx={status?.sx}
            />
          );
        },
      },
      {
        key: "recipient_name",
        headerName: "Recipient Name",
        fieldName: "recipient_name",
        renderType: DataTableV2RowRenderType.TEXT,
        width: "180px",
      },
      {
        key: "recipient_number",
        headerName: "Recipient Number",
        // fieldName: "to_number",
        fieldName: "recipient_number", // monika updated
        renderType: DataTableV2RowRenderType.TEXT,
        width: "180px",
      },
      {
        key: "content",
        headerName: "Template Content",
        fieldName: "content",
        renderType: DataTableV2RowRenderType.TEXT,
        width: "200px",
        onRowCellRender: (value, row: any) => {
          const content = row.content;

          if (!content?.length) return "--";

          const templates = content.map((template: any) => {
            let header = { format: null };
            let body = { text: "" };
            let noramlText = "";
            let footer = { text: "" };
            let buttons = { buttons: [] };

            if (template?.components && Array.isArray(template?.components)) {
              const components = template?.components;
              header = components?.find((c: any) => c.type === "HEADER") || {
                format: null,
              };
              body = components?.find((c: any) => c.type === "BODY") || {
                text: "",
              };
              noramlText = components?.[0].text;
              footer = components?.find((c: any) => c.type === "FOOTER") || {
                text: "",
              };
              buttons = components?.find((c: any) => c.type === "BUTTONS") || {
                buttons: [],
              };
            } else {
              header =
                template?.type === "HEADER" ? template : { format: null };
              body = template?.type === "BODY" ? template : { text: "" };
              noramlText = template?.text;
              footer = template?.type === "FOOTER" ? template : { text: "" };
              buttons =
                template?.type === "BUTTONS" ? template : { buttons: [] };
            }

            return {
              header,
              body,
              noramlText,
              footer,
              buttons,
              buttonCount: buttons.buttons?.length || 0,
            };
          });

          const firstTemplate = templates[0];
          const secondTemplate = templates[1];

          return (
            <Box>
              {firstTemplate?.header?.format && (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", fontWeight: "bold" }}
                >
                  Header: {firstTemplate.header.format}
                </Typography>
              )}
              {secondTemplate?.body?.text ? (
                <ReadMoreText
                  text={secondTemplate?.body?.text}
                  maxLines={2}
                  variant="body2"
                />
              ) : firstTemplate?.body?.text ? (
                <ReadMoreText
                  text={firstTemplate?.body?.text}
                  maxLines={2}
                  variant="body2"
                />
              ) : (
                <ReadMoreText
                  text={firstTemplate?.noramlText}
                  maxLines={2}
                  variant="body2"
                />
              )}
              {firstTemplate?.footer?.text && (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", color: "text.secondary" }}
                >
                  Footer: {firstTemplate.footer.text}
                </Typography>
              )}
              {firstTemplate?.buttonCount > 0 && (
                <Typography variant="caption" sx={{ color: "primary.main" }}>
                  Buttons: {firstTemplate.buttonCount}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        key: "template_name",
        headerName: "Template Name",
        fieldName: "template_name",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "create_ts",
        headerName: "Initiate At",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        onRowCellRender: (value, row: IMarketCampain) => {
          return row.create_ts ? ftDateTime(row.create_ts) : "--";
        },
      },
      {
        key: "read_ts",
        headerName: "Read At",
        fieldName: "read_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        onRowCellRender: (value, row: IMarketCampain) => {
          return row.read_ts ? ftDateTime(row.read_ts) : "--";
        },
      },
      {
        key: "send_ts",
        headerName: "Send At",
        fieldName: "send_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        onRowCellRender: (value, row: IMarketCampain) => {
          return row.send_ts ? ftDateTime(row.send_ts) : "--";
        },
      },
      {
        key: "delivered_ts",
        headerName: "Delivered At",
        fieldName: "delivered_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        onRowCellRender: (value, row: IMarketCampain) => {
          return row.delivered_ts ? ftDateTime(row.delivered_ts) : "--";
        },
      },
    ],
    rows: list,
    tableTabProps: {
      selectedTab: tabs.selectedTab,
      tabs: tabs.tabs,
      onTabChange: (newSelectedTab) => {
        setSelectedTab(newSelectedTab);
      },
    },
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,
    masterColumnResize: {
      values: masterColumnResize,
      onResize: onResizeMasterColumns,
    },
  };

  React.useEffect(() => {
    if (open && MarketCampaignUuid) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination,
    dateState.dates,
    tabs.selectedTab,
    searchState,
    open,
    MarketCampaignUuid,
  ]);

  React.useEffect(() => {
    if (!open) return;
    const modifiedWhatsappCounts = processWhatsappCounts(whatsappCounts);

    const tabsData = createTabsWithWhatsappcounts(
      STATUS_OPTIONS,
      modifiedWhatsappCounts,
    );

    setTableTabs(tabsData);
  }, [whatsappCounts]);

  React.useEffect(() => {
    if (!open || !MarketCampaignUuid || !company_outlet_uuid) return;
    dispatch(
      fetchWhatsappCountsAsync(
        MarketCampaignUuid,
        "market_campaign_uuid",
        dateState.dates.fromDate,
        dateState.dates.toDate,
        company_outlet_uuid,
      ),
    );
    return () => {
      dispatch(clearWhatsappCounts());
    };
  }, [
    dateState.dates.fromDate,
    dateState.dates.toDate,
    open,
    MarketCampaignUuid,
    company_outlet_uuid,
    dispatch,
  ]);

  const handleClose = () => {
    dispatch(clearWhatsappCounts());
    onClose();
  };

  return (
    <>
      <RightPanel
        heading="Market Campaign Analytics"
        open={open}
        width="95%"
        onClose={handleClose}
        actionButtons={() => {
          return (
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              spacing={1.5}
            >
              <Stack direction={"row"} spacing={1.5}>
                <Button variant="contained" onClick={handleClose}>
                  <Close sx={{ fontSize: 18, mr: 0.5 }} />
                  Close
                </Button>
              </Stack>
            </Stack>
          );
        }}
      >
        {campaignData && (
          <Grid>
            <StandardCard heading="" sx={{ mb: 3 }}>
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                flexWrap="wrap"
                sx={{
                  "& > *": {
                    minWidth: "fit-content",
                  },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CampaignIcon
                    sx={{
                      color: "primary.main",
                      fontSize: "1.5rem",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Campaign Name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        mt: 0.25,
                      }}
                    >
                      {campaignData.campaign_name || "--"}
                    </Typography>
                  </Box>
                </Stack>

                <Divider orientation="vertical" flexItem />

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    Status
                  </Typography>
                  <StatusRenderer status={campaignData.status || ""} />
                </Box>

                <Divider orientation="vertical" flexItem />

                <Stack direction="row" spacing={1} alignItems="center">
                  <ScheduleIcon
                    sx={{
                      color: "text.secondary",
                      fontSize: "1.2rem",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Sending Type
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "text.primary",
                        mt: 0.25,
                      }}
                    >
                      {(campaignData as any).sending_type || "--"}
                    </Typography>
                  </Box>
                </Stack>

                <Divider orientation="vertical" flexItem />

                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon
                    sx={{
                      color: "text.secondary",
                      fontSize: "1.2rem",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Created By
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "text.primary",
                        mt: 0.25,
                      }}
                    >
                      {(campaignData as any).created_by_name || "--"}
                    </Typography>
                  </Box>
                </Stack>

                <Divider orientation="vertical" flexItem />

                <Stack direction="row" spacing={1} alignItems="center">
                  <CalendarTodayIcon
                    sx={{
                      color: "text.secondary",
                      fontSize: "1.2rem",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Created Date
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        mt: 0.25,
                      }}
                    >
                      {campaignData.create_ts
                        ? ftDateTime(campaignData.create_ts)
                        : "--"}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </StandardCard>
          </Grid>
        )}

        {!(
          campaignData?.status?.toUpperCase() === "DONE" ||
          campaignData?.status?.toUpperCase() === "RUNNING" ||
          campaignData?.status?.toUpperCase() === "FAILED" ||
          list.length > 0 ||
          whatsappCounts.length > 0 ||
          loading === LoadState.InProgress
        ) ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
              textAlign: "center",
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Box
              component="img"
              src="https://static.vecteezy.com/system/resources/previews/007/872/974/non_2x/file-not-found-illustration-with-confused-people-holding-big-magnifier-search-no-result-data-not-found-concept-can-be-used-for-website-landing-page-animation-etc-vector.jpg"
              alt="No data"
              sx={{
                width: 450,
                height: 300,
                mb: 3,
                opacity: 0.8,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: (theme) => theme.palette.text.primary,
              }}
            >
              No Campaign Logs Found
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: (theme) => theme.palette.text.secondary,
                maxWidth: 400,
              }}
            >
              There are no logs available for this campaign yet. Please run or
              schedule the campaign first to see the logs.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <StatusBarGraph
                  sx={{
                    height: "100%",
                  }}
                  title="Message Status Distribution"
                  subheader="Count of messages by status"
                  isLoading={recordCountsLoading === LoadState.InProgress}
                  statusCounts={whatsappCounts}
                  selectedSeries={[]}
                  onSeriesChange={() => { }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PieChart
                  sx={{
                    height: "100%",
                  }}
                  title="Message Status Distribution"
                  subheader="Count of messages by status"
                  isLoading={recordCountsLoading === LoadState.InProgress}
                  statusCounts={whatsappCounts}
                  selectedSeries={[]}
                  onSeriesChange={() => { }}
                />
              </Grid>
            </Grid>

            <Grid mt={3}>
              <DataTableV2 {...opportunitesTableProps} />
            </Grid>
          </>
        )}

        {openView && (
          <ViewWhatsappTemplateDetails
            type={"ANALYTICS"}
            open={true}
            metaTemplate={openView}
            onClose={() => setOpenView(null)}
          />
        )}

        {openWhatsapp.campaignLogsData &&
          openWhatsapp.campaignLogsData?.recipient_number && (
            <ViewWhatsappDetails
              open={true}
              onClose={() =>
                setOpenWhatsapp({ leadData: null, campaignLogsData: null })
              }
              leadDetails={openWhatsapp.leadData}
              campaignLogsData={openWhatsapp.campaignLogsData}
              phoneNumber={openWhatsapp.campaignLogsData?.recipient_number}
            />
          )}
      </RightPanel>
    </>
  );
};
